import { osnovaApi } from '../base';
import type { ContractMaterialNorm, AccumDetail, AccumEntriesPage, AccumEntry, AccumLedger, AccumUploadDetail, ContractEstimate, ContractLsr, ContractLsrItem, LsrImportStats, ContractEstimateOption, FgiscsPeriod, FgiscsPeriodDiff, FsnbCoefficientCard, FsnbCoefficientHit, FsnbEdition, FsnbEditionChange, FsnbEditionsResponse, FsnbRegistryItem, FsnbResourceCard, FsnbResourceHit, FsnbSearchResponse, FsnbWorkCard, FsnbWorkHit, FieldKsDraft, GsCheck, GsCheckDetail, GsDiscrepanciesResponse, KsAct, KsActDetail, KsDiscrepanciesResponse, KsDraftDetail, KsEstimateItemOption, KsPackage, KsPackageAct, KsPackageDetail, KsPackageDoc, KsPackageDocsUploadResult, KsPackageForm, KsPackageIssuesResponse, KsPackageLinesResponse, KsPackageMaterialsResponse, KsPackageOverride, OzhrPayload, OzhrRequisites, PtoContractBrief, PtoProject, RateCheck, RateCheckDetail, RateDiscrepanciesResponse, RegisterDetail, RegisterSession, SmetaChatMessage, SmetaChatSession, SmetaContextOptions, SmetaInfo, RateCandidates, TenderEstimate, TenderEstimateDetail, TenderItemCodeResult, VolumeCheck, VolumeCheckDetail, WriteOffCheck, WriteOffCheckDetail, ZviCheck, ZviCheckDetail } from '../types';

export const smetaApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Локальные сметы договора — эталон мира B. */
    listContractLsr: b.query<{ items: ContractLsr[] }, string>({
      query: (contractId) => `/agents/smeta/contracts/${contractId}/lsr`,
      providesTags: (_r, _e, id) => [{ type: 'ContractLsr', id }],
    }),

    /** Позиции одной сметы: с ОЗП/ЭММ/ЗПМ и трудозатратами. */
    listContractLsrItems: b.query<{ items: ContractLsrItem[] }, { contractId: string; lsrId: string }>({
      query: ({ contractId, lsrId }) =>
        `/agents/smeta/contracts/${contractId}/lsr/${lsrId}/items`,
      providesTags: (_r, _e, a) => [{ type: 'ContractLsr', id: a.lsrId }],
    }),

    /** Импорт книги смет (выгрузка Гранд-Сметы, лист = смета). Повторная
     *  загрузка обновляет те же сметы по номеру ЛСР. */
    importContractLsr: b.mutation<LsrImportStats, { contractId: string; form: FormData }>({
      query: ({ contractId, form }) => ({
        url: `/agents/smeta/contracts/${contractId}/lsr/import`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: (_r, _e, a) => [
        { type: 'ContractLsr', id: a.contractId },
        'ContractEstimate',
        'MaterialNorm',
        'MtoRun',
      ],
    }),

    deleteContractLsr: b.mutation<{ ok: boolean }, { contractId: string; lsrId: string }>({
      query: ({ contractId, lsrId }) => ({
        url: `/agents/smeta/contracts/${contractId}/lsr/${lsrId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, a) => [{ type: 'ContractLsr', id: a.contractId }],
    }),

    /** Каркас агента ОСНОВА.Сметчик: карточка + список запланированных функций. */
    getSmetaInfo: b.query<SmetaInfo, void>({
      query: () => '/agents/smeta/info',
    }),

    // ОСНОВА.Сметчик, Функция 1 — проверка объёмов работ.
    listVolumeChecks: b.query<{ items: VolumeCheck[] }, { limit?: number; offset?: number } | void>({
      query: (p) => ({ url: '/agents/smeta/volume-checks', params: p ?? undefined }),
      providesTags: ['VolumeCheck'],
    }),
    getVolumeCheck: b.query<VolumeCheckDetail, string>({
      query: (id) => `/agents/smeta/volume-checks/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'VolumeCheck', id }],
    }),
    createVolumeCheck: b.mutation<{ check_id: string; status: string }, FormData>({
      query: (form) => ({ url: '/agents/smeta/volume-checks', method: 'POST', body: form }),
      invalidatesTags: ['VolumeCheck'],
    }),
    deleteVolumeCheck: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/volume-checks/${id}`, method: 'DELETE' }),
      invalidatesTags: ['VolumeCheck'],
    }),
    annotateVolumeDiscrepancy: b.mutation<
      { ok: boolean },
      { id: string; checkId: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/smeta/discrepancies/${id}`,
        method: 'PATCH',
        body: { decision, comment },
      }),
      invalidatesTags: (_r, _e, { checkId }) => [{ type: 'VolumeCheck', id: checkId }],
    }),
    /** Выгрузка XLSX-отчёта о проверке объёмов (Функция 1). Отчёт собирается
     *  на лету из volume_checks + volume_discrepancies и рендерится doc-processor'ом
     *  (генерация с нуля, бланка нет). Доступно после завершения (status 'completed').
     *  Имя файла backend формирует ASCII-safe (`volume_check_<id>.xlsx`).
     *  Лениво (`useLazyExportVolumeCheckXlsxQuery`): запрос строго по клику. */
    exportVolumeCheckXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/smeta/volume-checks/${id}/export-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ОСНОВА.Сметчик, Функция 4 — списание материалов / М-29 (ADR-0005).
    listWriteOffChecks: b.query<
      { items: WriteOffCheck[] },
      { limit?: number; offset?: number } | void
    >({
      query: (p) => ({ url: '/agents/smeta/write-off-checks', params: p ?? undefined }),
      providesTags: ['WriteOffCheck'],
    }),
    getWriteOffCheck: b.query<WriteOffCheckDetail, string>({
      query: (id) => `/agents/smeta/write-off-checks/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'WriteOffCheck', id }],
    }),
    createWriteOffCheck: b.mutation<{ check_id: string; status: string }, FormData>({
      query: (form) => ({ url: '/agents/smeta/write-off-checks', method: 'POST', body: form }),
      invalidatesTags: ['WriteOffCheck'],
    }),
    deleteWriteOffCheck: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/write-off-checks/${id}`, method: 'DELETE' }),
      invalidatesTags: ['WriteOffCheck'],
    }),
    annotateWriteOffDiscrepancy: b.mutation<
      { ok: boolean },
      { id: string; checkId: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/smeta/write-off-discrepancies/${id}`,
        method: 'PATCH',
        body: { decision, comment },
      }),
      invalidatesTags: (_r, _e, { checkId }) => [{ type: 'WriteOffCheck', id: checkId }],
    }),
    /** Выгрузка XLSX-отчёта М-29 (Функция 4). На лету из write_off_*; доступно
     *  после завершения (status 'completed'). Имя ASCII-safe (`writeoff_m29_<id>.xlsx`).
     *  Лениво (`useLazyExportWriteOffCheckXlsxQuery`): по клику. */
    exportWriteOffCheckXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/smeta/write-off-checks/${id}/export-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ОСНОВА.Сметчик, Функция 7 — накопительные таблицы / реестры (ADR-0007).
    listRegisters: b.query<
      { items: RegisterSession[] },
      { limit?: number; offset?: number } | void
    >({
      query: (p) => ({ url: '/agents/smeta/registers', params: p ?? undefined }),
      providesTags: ['RegisterSession'],
    }),
    getRegister: b.query<RegisterDetail, string>({
      query: (id) => `/agents/smeta/registers/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'RegisterSession', id }],
    }),
    createRegister: b.mutation<{ session_id: string; status: string }, FormData>({
      query: (form) => ({ url: '/agents/smeta/registers', method: 'POST', body: form }),
      invalidatesTags: ['RegisterSession'],
    }),
    deleteRegister: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/registers/${id}`, method: 'DELETE' }),
      invalidatesTags: ['RegisterSession'],
    }),
    /** Выгрузка многолистового XLSX накопительного реестра (Функция 7). На лету
     *  из register_*; доступно после завершения (status 'completed'). Имя
     *  ASCII-safe (`register_<id>.xlsx`). Лениво (`useLazyExportRegisterXlsxQuery`). */
    exportRegisterXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/smeta/registers/${id}/export-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ОСНОВА.Сметчик, Функция 7 (stateful) — накопительный учёт ПА (ADR-0011).
    listAccumulations: b.query<
      { items: AccumLedger[] },
      { limit?: number; offset?: number } | void
    >({
      query: (p) => ({ url: '/agents/smeta/accumulations', params: p ?? undefined }),
      providesTags: ['AccumLedger'],
    }),
    getAccumulation: b.query<AccumDetail, string>({
      query: (id) => `/agents/smeta/accumulations/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'AccumLedger', id }, 'AccumUpload'],
    }),
    /** Разовый seed ленты из существующей книги «Накопительный учет ПА». */
    seedAccumulation: b.mutation<
      { upload_id: string; ledger_id: string; status: string },
      FormData
    >({
      query: (form) => ({ url: '/agents/smeta/accumulations/seed', method: 'POST', body: form }),
      invalidatesTags: ['AccumLedger', 'AccumUpload'],
    }),
    /** Ежемесячная пачка первичных актов → дозаполнение ленты. */
    uploadAccumulationPack: b.mutation<
      { upload_id: string; status: string },
      { id: string; form: FormData }
    >({
      query: ({ id, form }) => ({
        url: `/agents/smeta/accumulations/${id}/uploads`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AccumLedger', id }, 'AccumUpload'],
    }),
    getAccumulationUpload: b.query<AccumUploadDetail, string>({
      query: (id) => `/agents/smeta/accumulations/uploads/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'AccumUpload', id }],
    }),
    deleteAccumulation: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/accumulations/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AccumLedger'],
    }),
    /** Откат загрузки: убирает дописанные ею строки ленты + саму загрузку. */
    deleteAccumulationUpload: b.mutation<
      { ok: boolean; removed_entries: number },
      string
    >({
      query: (id) => ({
        url: `/agents/smeta/accumulations/uploads/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AccumLedger', 'AccumUpload'],
    }),
    /**
     * Страница ленты «данные ПА» с фильтрами.
     *
     * Целиком лента не отдаётся: на проде 21,6 тыс. строк.
     */
    listAccumulationEntries: b.query<
      AccumEntriesPage,
      {
        id: string;
        limit?: number;
        offset?: number;
        source?: string;
        titul?: string;
        pa_no?: string;
        search?: string;
        missing_mark?: boolean;
        manual_mark?: boolean;
      }
    >({
      query: ({ id, ...params }) => ({
        url: `/agents/smeta/accumulations/${id}/entries`,
        params,
      }),
      providesTags: (_r, _e, { id }) => [{ type: 'AccumLedger', id }],
    }),
    /**
     * Ручная марка РД строкам ленты (уточнение сметного отдела 03.09.2026:
     * «марку у старых строк проставим вручную»). Решение переживает
     * пересборку ленты — хранится отдельно от самих строк.
     */
    setAccumulationEntriesMark: b.mutation<
      { updated: number; items: AccumEntry[] },
      { id: string; entry_ids: string[]; mark_rd: string | null }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/smeta/accumulations/${id}/entries/mark`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AccumLedger', id }],
    }),
    /** Регенерация книги накопительного учёта (4 листа). Лениво. */
    exportAccumulationXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/smeta/accumulations/${id}/export-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ОСНОВА.Сметчик, Функция 2 — поквартальные периоды ФГИС ЦС (ADR-0003).
    listFgiscsPeriods: b.query<{ items: FgiscsPeriod[] }, { status?: string } | void>({
      query: (p) => ({ url: '/agents/smeta/fgiscs/periods', params: p ?? undefined }),
      providesTags: ['FgiscsPeriod'],
    }),
    getFgiscsPeriodDiff: b.query<FgiscsPeriodDiff, number>({
      query: (id) => `/agents/smeta/fgiscs/periods/${id}/diff`,
      providesTags: (_r, _e, id) => [{ type: 'FgiscsPeriod', id }],
    }),
    activateFgiscsPeriod: b.mutation<{ ok: boolean; archived_count: number }, number>({
      query: (id) => ({ url: `/agents/smeta/fgiscs/periods/${id}/activate`, method: 'POST' }),
      // Активация меняет статус самого периода + переводит прошлый active
      // в archived → инвалидируем всю таблицу.
      invalidatesTags: ['FgiscsPeriod'],
    }),
    // Возврат архивного периода в действующие (только роль «Начальник СЦО»).
    restoreArchivedFgiscsPeriod: b.mutation<{ ok: boolean; archived_count: number }, number>({
      query: (id) => ({ url: `/agents/smeta/fgiscs/periods/${id}/restore`, method: 'POST' }),
      invalidatesTags: ['FgiscsPeriod'],
    }),
    // Массовая активация всех pending_review (новейший на разрез → active).
    activateAllFgiscsPeriods: b.mutation<{ ok: boolean; activated: number }, void>({
      query: () => ({ url: '/agents/smeta/fgiscs/periods/activate-all', method: 'POST' }),
      invalidatesTags: ['FgiscsPeriod'],
    }),
    // Регионы с активным РИМ-периодом — для выбора региона при загрузке ЛСР
    // (строка обязана совпадать со smeta_index_periods.region, Эпик А).
    listRimRegions: b.query<{ regions: string[] }, void>({
      query: () => '/agents/smeta/fgiscs/rim-regions',
      providesTags: ['FgiscsPeriod'],
    }),
    // Типы объектов («Тип / Под-тип») из активного письма Минстроя — для выбора
    // типа объекта при загрузке ЛСР (БИМ-индекс ФЕР по региону × типу объекта).
    listObjectTypes: b.query<{ object_types: string[] }, void>({
      query: () => '/agents/smeta/fgiscs/object-types',
      providesTags: ['FgiscsPeriod'],
    }),

    // ОСНОВА.Сметчик — «Нормативная база»: редакции ФСНБ-2022 и справочник.
    listFsnbEditions: b.query<FsnbEditionsResponse, void>({
      query: () => '/agents/smeta/fsnb/editions',
      providesTags: ['FsnbEdition'],
    }),
    /** Архив дополнения, как скачан с ФГИС ЦС (поле `file`). Разбор — фоном. */
    uploadFsnbEdition: b.mutation<{ edition: FsnbEdition }, FormData>({
      query: (form) => ({ url: '/agents/smeta/fsnb/editions', method: 'POST', body: form }),
      invalidatesTags: ['FsnbEdition', 'FsnbRegistry'],
    }),
    listFsnbEditionChanges: b.query<
      { items: FsnbEditionChange[]; total: number },
      {
        id: string;
        entity?: string;
        change?: string;
        kind?: string;
        q?: string;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ id, ...params }) => ({ url: `/agents/smeta/fsnb/editions/${id}/changes`, params }),
      providesTags: (_r, _e, a) => [{ type: 'FsnbEdition', id: a.id }],
    }),
    /** Ввести в действие: прежняя действующая уходит в архив, расчёты —
     *  на новую. Меняет весь справочник для поиска и карточек. */
    activateFsnbEdition: b.mutation<
      { edition: FsnbEdition; previous: string | null; carried_over: Array<[string, number]> },
      string
    >({
      query: (id) => ({ url: `/agents/smeta/fsnb/editions/${id}/activate`, method: 'POST' }),
      invalidatesTags: ['FsnbEdition', 'FsnbRegistry', 'FsnbBase'],
    }),
    deleteFsnbEdition: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/fsnb/editions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['FsnbEdition', 'FsnbRegistry'],
    }),
    retryFsnbEdition: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/fsnb/editions/${id}/retry`, method: 'POST' }),
      invalidatesTags: ['FsnbEdition'],
    }),
    searchFsnbWorks: b.query<
      FsnbSearchResponse<FsnbWorkHit>,
      { q: string; kind?: string; edition?: string; limit?: number; offset?: number }
    >({
      query: (params) => ({ url: '/agents/smeta/fsnb/works', params }),
      providesTags: ['FsnbBase'],
    }),
    getFsnbWork: b.query<
      { edition: string; card: FsnbWorkCard },
      { base_type: string; code: string; edition?: string }
    >({
      query: (params) => ({ url: '/agents/smeta/fsnb/work', params }),
      providesTags: ['FsnbBase'],
    }),
    searchFsnbResources: b.query<
      FsnbSearchResponse<FsnbResourceHit>,
      { q: string; kind?: string; edition?: string; limit?: number; offset?: number }
    >({
      query: (params) => ({ url: '/agents/smeta/fsnb/resources', params }),
      providesTags: ['FsnbBase'],
    }),
    getFsnbResource: b.query<
      { edition: string; card: FsnbResourceCard },
      { source: string; code: string; edition?: string }
    >({
      query: (params) => ({ url: '/agents/smeta/fsnb/resource', params }),
      providesTags: ['FsnbBase'],
    }),
    searchFsnbCoefficients: b.query<
      FsnbSearchResponse<FsnbCoefficientHit> & { loaded: boolean },
      { q: string; kind?: string; edition?: string; limit?: number; offset?: number }
    >({
      query: (params) => ({ url: '/agents/smeta/fsnb/coefficients', params }),
      providesTags: ['FsnbBase'],
    }),
    getFsnbCoefficient: b.query<
      { edition: string; card: FsnbCoefficientCard },
      { code: string; edition?: string }
    >({
      query: (params) => ({ url: '/agents/smeta/fsnb/coefficient', params }),
      providesTags: ['FsnbBase'],
    }),
    listFsnbRegistry: b.query<{ items: FsnbRegistryItem[]; source_url: string }, void>({
      query: () => '/agents/smeta/fsnb/registry',
      providesTags: ['FsnbRegistry'],
    }),
    /** «Проверить сейчас»: один запрос к открытому списку дополнений ФГИС ЦС. */
    checkFsnbRegistry: b.mutation<
      { check: { seen: number; new_items: number; notified: string | null } },
      void
    >({
      query: () => ({ url: '/agents/smeta/fsnb/registry/check', method: 'POST' }),
      invalidatesTags: ['FsnbRegistry'],
    }),

    // ОСНОВА.Сметчик, Функция 2, Stage C — применённые подрядчиком расценки
    // (выгрузка Гранд-Сметы в xlsx). Парсер в doc-processor.
    listRateChecks: b.query<{ items: RateCheck[] }, { limit?: number; offset?: number } | void>({
      query: (p) => ({ url: '/agents/smeta/rate-checks', params: p ?? undefined }),
      providesTags: ['RateCheck'],
    }),
    getRateCheck: b.query<RateCheckDetail, string>({
      query: (id) => `/agents/smeta/rate-checks/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'RateCheck', id }],
    }),
    uploadRateCheck: b.mutation<
      { check_id: string; sheet_count: number; position_count: number; warnings?: string[] },
      FormData
    >({
      query: (form) => ({ url: '/agents/smeta/rate-checks', method: 'POST', body: form }),
      invalidatesTags: ['RateCheck'],
    }),
    deleteRateCheck: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/rate-checks/${id}`, method: 'DELETE' }),
      invalidatesTags: ['RateCheck'],
    }),

    // Stage D / E — движок проверки расценок и HITL-разметка расхождений.
    /** Поставить job AnalyzeRateCheck — анализ async, статус смотри через
     *  getRateCheck (поле check.status: analyzing | analyzed | analyze_failed). */
    analyzeRateCheck: b.mutation<
      { queued: boolean; check_id: string },
      string
    >({
      query: (id) => ({ url: `/agents/smeta/rate-checks/${id}/analyze`, method: 'POST' }),
      // Статус check изменится → перетянуть getRateCheck. Discrepancies станут
      // актуальны после завершения analyze, тег инвалидируется отдельно через
      // refetch на странице.
      invalidatesTags: (_r, _e, id) => [
        { type: 'RateCheck', id },
        { type: 'RateDiscrepancy', id },
      ],
    }),
    listRateDiscrepancies: b.query<
      RateDiscrepanciesResponse,
      { checkId: string; severity?: string; decision?: string }
    >({
      query: ({ checkId, severity, decision }) => ({
        url: `/agents/smeta/rate-checks/${checkId}/discrepancies`,
        params: { severity, decision },
      }),
      providesTags: (_r, _e, { checkId }) => [{ type: 'RateDiscrepancy', id: checkId }],
    }),
    annotateRateDiscrepancy: b.mutation<
      { ok: boolean },
      { id: string; checkId: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/smeta/rate-discrepancies/${id}`,
        method: 'PATCH',
        body: { decision, comment },
      }),
      invalidatesTags: (_r, _e, { checkId }) => [
        { type: 'RateDiscrepancy', id: checkId },
      ],
    }),
    /** Выгрузка XLSX-отчёта о проверке расценок (Функция 2). Отчёт собирается
     *  на лету из applied_rates + rate_discrepancies и рендерится doc-processor'ом
     *  (генерация с нуля, бланка нет). Доступно после анализа (status 'analyzed').
     *  Authorization ставит baseQuery; имя файла backend формирует ASCII-safe
     *  (`rate_check_<id>.xlsx`), так что потеря Content-Disposition в RTK Query не
     *  критична. `no-store` — отчёт мог быть перегенерирован re-analyze'ом.
     *  Лениво (`useLazyExportRateCheckXlsxQuery`): запрос строго по клику. */
    exportRateCheckXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/smeta/rate-checks/${id}/export-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ОСНОВА.Сметчик, Функция 3 — сверка КС-2/КС-3 с договорной сметой (ADR-0004).
    listContractEstimates: b.query<{ items: ContractEstimate[] }, { contractId: string }>({
      query: ({ contractId }) => ({
        url: '/agents/smeta/contract-estimates',
        params: { contract_id: contractId },
      }),
      providesTags: ['ContractEstimate'],
    }),
    // Плоский список всех договорных смет (с номером/предметом договора) для
    // выпадающего выбора эталона — используется в форме ЗВИ (Функция 6).
    listContractEstimateOptions: b.query<{ items: ContractEstimateOption[] }, void>({
      query: () => ({ url: '/agents/smeta/contract-estimate-options' }),
      providesTags: ['ContractEstimate'],
    }),
    uploadContractEstimate: b.mutation<
      { estimate_id: string; version_no: number; item_count: number; warnings?: string[] },
      FormData
    >({
      query: (form) => ({
        url: '/agents/smeta/contract-estimates',
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['ContractEstimate', 'ContractLsr', 'MaterialNorm', 'MtoRun'],
    }),
    listKsActs: b.query<{ items: KsAct[] }, { limit?: number; offset?: number } | void>({
      query: (p) => ({ url: '/agents/smeta/ks-acts', params: p ?? undefined }),
      providesTags: ['KsAct'],
    }),
    getKsAct: b.query<KsActDetail, string>({
      query: (id) => `/agents/smeta/ks-acts/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'KsAct', id }],
    }),
    uploadKsAct: b.mutation<
      { act_ids: string[]; act_count: number; position_count: number; warnings?: string[] },
      FormData
    >({
      query: (form) => ({ url: '/agents/smeta/ks-acts', method: 'POST', body: form }),
      invalidatesTags: ['KsAct'],
    }),
    deleteKsAct: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/ks-acts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['KsAct'],
    }),
    analyzeKsAct: b.mutation<{ queued: boolean; act_id: string }, string>({
      query: (id) => ({ url: `/agents/smeta/ks-acts/${id}/analyze`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'KsAct', id },
        { type: 'KsDiscrepancy', id },
      ],
    }),
    listKsDiscrepancies: b.query<
      KsDiscrepanciesResponse,
      { actId: string; severity?: string; decision?: string }
    >({
      query: ({ actId, severity, decision }) => ({
        url: `/agents/smeta/ks-acts/${actId}/discrepancies`,
        params: { severity, decision },
      }),
      providesTags: (_r, _e, { actId }) => [{ type: 'KsDiscrepancy', id: actId }],
    }),
    annotateKsDiscrepancy: b.mutation<
      { ok: boolean },
      { id: string; actId: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/smeta/ks-discrepancies/${id}`,
        method: 'PATCH',
        body: { decision, comment },
      }),
      invalidatesTags: (_r, _e, { actId }) => [
        { type: 'KsDiscrepancy', id: actId },
        { type: 'KsAct', id: actId },
      ],
    }),
    exportKsCheckXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/smeta/ks-acts/${id}/export-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ОСНОВА.Сметчик — пакет акта (КС-2 / КС-3 / КС-6а + спутники).
    //
    // Группа роутов регистрируется только при `OSNOVA_KS_PACKAGE_ENABLED=1`;
    // при выключенном флаге любой запрос сюда получает 404 от роутера. Именно
    // так UI и узнаёт о доступности функции (как ОЖР/КС-2 у Прораба) — в
    // `/agents/smeta/info` флага нет.
    //
    // Сборка НИКОГДА не синхронная: POST /build ставит `Job::BuildKsPackage` и
    // отдаёт `{queued:true}`, а прогресс виден поллингом карточки
    // (`status`: building → built | build_failed, причина — в `error_message`).
    listKsPackages: b.query<
      { items: KsPackage[] },
      {
        contract_id?: string;
        project_id?: string;
        status?: string;
        source?: string;
        period_from?: string;
        period_to?: string;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (p) => ({ url: '/agents/smeta/ks-packages', params: p ?? undefined }),
      providesTags: ['KsPackage'],
    }),
    /** Карточка: шапка + акты + подписи + своды по статьям + счётчики находок. */
    getKsPackage: b.query<KsPackageDetail, string>({
      query: (id) => `/agents/smeta/ks-packages/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'KsPackage', id }],
    }),
    /** Завести пакет (повторный POST с тем же `ks3_no` обновляет шапку). */
    createKsPackage: b.mutation<
      { package: KsPackage },
      {
        contract_id: string;
        ks3_no: string;
        doc_date: string;
        period_from: string;
        period_to: string;
        source?: string;
        project_id?: string;
        profile_id?: string;
        contract_estimate_id?: string;
        notes?: string;
      }
    >({
      query: (body) => ({ url: '/agents/smeta/ks-packages', method: 'POST', body }),
      invalidatesTags: ['KsPackage'],
    }),
    deleteKsPackage: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/ks-packages/${id}`, method: 'DELETE' }),
      invalidatesTags: ['KsPackage'],
    }),
    /** Поставить сборку в очередь. Меняет статус на `building` сразу, поэтому
     *  инвалидируем и строки с находками — прошлый прогон уже неактуален. */
    buildKsPackage: b.mutation<{ queued: boolean; package_id: string }, string>({
      query: (id) => ({ url: `/agents/smeta/ks-packages/${id}/build`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'KsPackage', id },
        { type: 'KsPackageLine', id },
        { type: 'KsPackageIssue', id },
        // Сборка пересчитывает материалы строк (норма/факт/сумма) — таблица
        // материалов мастера обязана обновиться вместе со строками.
        { type: 'KsPackageMaterial', id },
      ],
    }),
    /** Завести/обновить акт КС-2 внутри пакета (ключ идемпотентности — `act_no`).
     *  Без хотя бы одного акта сборщику некуда класть строки: он идёт по актам
     *  пакета, а сам их не создаёт. */
    upsertKsPackageAct: b.mutation<
      { act: KsPackageAct },
      {
        packageId: string;
        act_no: string;
        doc_date: string;
        period_from: string;
        period_to: string;
        ord?: number;
        title_id?: string;
        lsr_id?: string;
        estimate_ref?: string;
        rd_mark?: string;
        work_kind?: string;
        is_correction?: boolean;
        corrects_act_ref?: string;
      }
    >({
      query: ({ packageId, ...body }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/acts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { packageId }) => [{ type: 'KsPackage', id: packageId }],
    }),
    deleteKsPackageAct: b.mutation<{ ok: boolean }, { packageId: string; actId: string }>({
      query: ({ packageId, actId }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/acts/${actId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { packageId }) => [
        { type: 'KsPackage', id: packageId },
        { type: 'KsPackageLine', id: packageId },
      ],
    }),
    /** Строки пакета (или одного акта). Хвост итогов акта приходит только при
     *  фильтре по акту — он и определён на акте, а не на пакете. */
    listKsPackageLines: b.query<
      KsPackageLinesResponse,
      { packageId: string; actId?: string }
    >({
      query: ({ packageId, actId }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/lines`,
        params: { act_id: actId || undefined },
      }),
      providesTags: (_r, _e, { packageId }) => [{ type: 'KsPackageLine', id: packageId }],
    }),
    /** Находки текущей пересборки (`only_current: false` — вся история решений). */
    listKsPackageIssues: b.query<
      KsPackageIssuesResponse,
      { packageId: string; severity?: string; decision?: string; only_current?: boolean }
    >({
      query: ({ packageId, severity, decision, only_current }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/issues`,
        params: { severity, decision, only_current },
      }),
      providesTags: (_r, _e, { packageId }) => [{ type: 'KsPackageIssue', id: packageId }],
    }),
    /** HITL-разметка находки. Решение принадлежит человеку и переживает
     *  пересборку, но `blocking` в карточке пакета от него меняется. */
    annotateKsPackageIssue: b.mutation<
      { ok: boolean },
      { id: string; packageId: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/smeta/ks-package-issues/${id}`,
        method: 'PATCH',
        body: { decision, comment },
      }),
      invalidatesTags: (_r, _e, { packageId }) => [
        { type: 'KsPackageIssue', id: packageId },
        { type: 'KsPackage', id: packageId },
      ],
    }),
    /** Ручной матч строки на позицию эталона. Пишется В ПРАВКИ, а не в строку:
     *  пересборка перезаписывает состав из источников, и привязка, проставленная
     *  прямо в строку, исчезла бы на ближайшем `build`. Отсюда `rebuild_required`
     *  в ответе — в самой строке матч появится только после пересборки. */
    matchKsPackageLine: b.mutation<
      { override: KsPackageOverride; line_id: string; rebuild_required: boolean },
      {
        packageId: string;
        lineId: string;
        estimate_item_id?: string;
        lsr_item_id?: string;
        reason?: string;
      }
    >({
      query: ({ packageId, lineId, ...body }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/lines/${lineId}/match`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { packageId }) => [
        { type: 'KsPackageOverride', id: packageId },
        { type: 'KsPackageLine', id: packageId },
      ],
    }),
    listKsPackageOverrides: b.query<{ items: KsPackageOverride[] }, string>({
      query: (packageId) => `/agents/smeta/ks-packages/${packageId}/overrides`,
      providesTags: (_r, _e, packageId) => [{ type: 'KsPackageOverride', id: packageId }],
    }),
    /** Завести/переписать ручную правку. Для денежных полей backend требует
     *  `reason` — без основания подгонка копеек через год неотличима от ошибки. */
    upsertKsPackageOverride: b.mutation<
      { override: KsPackageOverride; rebuild_required: boolean },
      {
        packageId: string;
        target: string;
        anchor: string;
        field: string;
        value: unknown;
        act_id?: string;
        reason?: string;
      }
    >({
      query: ({ packageId, ...body }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/overrides`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { packageId }) => [
        { type: 'KsPackageOverride', id: packageId },
      ],
    }),
    deleteKsPackageOverride: b.mutation<
      { ok: boolean; rebuild_required: boolean },
      { packageId: string; overrideId: string }
    >({
      query: ({ packageId, overrideId }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/overrides/${overrideId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { packageId }) => [
        { type: 'KsPackageOverride', id: packageId },
      ],
    }),

    /** Материалы строк пакета в разрезе принадлежности + свод сумм.
     *
     *  Свод считает backend: «свои / давальческие / оборудование Заказчика» —
     *  это разные формы и разные деньги (свои входят в итог акта, давальческие
     *  нет), и фронт не должен решать, какие виды бывают. */
    listKsPackageMaterials: b.query<
      KsPackageMaterialsResponse,
      { packageId: string; actId?: string }
    >({
      query: ({ packageId, actId }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/materials`,
        params: actId ? { act_id: actId } : undefined,
      }),
      providesTags: (_r, _e, { packageId }) => [{ type: 'KsPackageMaterial', id: packageId }],
    }),

    // Первичка пакета (этап 10). MULTIPART: body — голый FormData, без headers
    // (тот же приём, что у остальных загрузок сметчика: проставь Content-Type
    // руками — потеряется boundary, и backend получит нечитаемое тело).
    // Поля: `doc_kind` (обязателен), `supply`, `kind`, `act_id` и сколько
    // угодно `file`.
    listKsPackageDocs: b.query<
      { items: KsPackageDoc[] },
      { packageId: string; doc_kind?: string }
    >({
      query: ({ packageId, doc_kind }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/documents`,
        params: doc_kind ? { doc_kind } : undefined,
      }),
      providesTags: (_r, _e, { packageId }) => [{ type: 'KsPackageDoc', id: packageId }],
    }),
    /** Загрузить накладные/акты передачи. Ответ говорит, сколько позиций село
     *  на материалы строк, а сколько осталось находками — и что пакет пора
     *  пересобрать (состав материалов изменился, итоги считает сборщик). */
    uploadKsPackageDocs: b.mutation<
      KsPackageDocsUploadResult,
      { packageId: string; form: FormData }
    >({
      query: ({ packageId, form }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/documents`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: (_r, _e, { packageId }) => [
        { type: 'KsPackageDoc', id: packageId },
        // Позиции накладной ложатся на материалы строк — их перечень меняется
        // сразу, ещё до пересборки.
        { type: 'KsPackageMaterial', id: packageId },
        // Импорт пишет находки (непривязанные позиции, неоднозначный матч) и
        // трогает сальдо — карточку с находками перечитываем.
        { type: 'KsPackageIssue', id: packageId },
        { type: 'KsPackage', id: packageId },
      ],
    }),

    // Печатные формы комплекта. Рендер, как и сборка, идёт очередью: КС-6а —
    // это 7,8 тыс. строк на 66 колонок, и синхронный вызов занял бы
    // единственный воркер doc-processor на всё это время.
    listKsPackageForms: b.query<
      { items: KsPackageForm[] },
      { packageId: string; history?: boolean }
    >({
      query: ({ packageId, history }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/forms`,
        params: history ? { history: true } : undefined,
      }),
      providesTags: (_r, _e, { packageId }) => [{ type: 'KsPackageForm', id: packageId }],
    }),
    /** Поставить выпуск форм в очередь. Пустой `form_codes` = весь включённый
     *  в профиль договора комплект; непустой — только эти бланки. */
    renderKsPackageForms: b.mutation<
      { queued: boolean; package_id: string },
      { packageId: string; form_codes?: string[] }
    >({
      query: ({ packageId, form_codes }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/render`,
        method: 'POST',
        body: { form_codes: form_codes ?? [] },
      }),
      invalidatesTags: (_r, _e, { packageId }) => [
        { type: 'KsPackageForm', id: packageId },
        { type: 'KsPackage', id: packageId },
        { type: 'KsPackageIssue', id: packageId },
      ],
    }),
    /** Выпустить комплект ОДНОГО акта одной кнопкой (спутники КС-2: ВВМП/ВВДМ/
     *  ВСО в мире A, Приложение №1 + М-19 + М-29 в мире B).
     *
     *  Отдельный адрес, а не флаг в теле общего выпуска: комплект акта обязан
     *  уехать заказчику ОДНОЙ редакцией, а шесть отдельных запросов дали бы
     *  шесть разных `rebuild_no` при первой же параллельной пересборке.
     *  Формы уровня договора (КС-3, КС-6а, реестр) сюда не принимаются —
     *  backend отобьёт их явной ошибкой. */
    renderKsPackageActForms: b.mutation<
      { queued: boolean; package_id: string; act_id: string | null },
      { packageId: string; actId: string; form_codes?: string[] }
    >({
      query: ({ packageId, actId, form_codes }) => ({
        url: `/agents/smeta/ks-packages/${packageId}/acts/${actId}/render`,
        method: 'POST',
        body: { form_codes: form_codes ?? [] },
      }),
      invalidatesTags: (_r, _e, { packageId }) => [
        { type: 'KsPackageForm', id: packageId },
        { type: 'KsPackage', id: packageId },
        { type: 'KsPackageIssue', id: packageId },
      ],
    }),

    // ОСНОВА.Сметчик, Функция 5 — сметный расчёт по тендеру (ADR-0006).
    // Загрузка перечня работ → backend строит ЛСР по нормативу (async Job) →
    // позиции + ошибки + бенчмарк с аналогами + выгрузка XLSX. Статус —
    // поллингом getTenderEstimate (поле status), как rate/write-off.
    listTenderEstimates: b.query<
      { items: TenderEstimate[] },
      { limit?: number; offset?: number } | void
    >({
      query: (p) => ({ url: '/agents/smeta/tender-estimates', params: p ?? undefined }),
      providesTags: ['TenderEstimate'],
    }),
    getTenderEstimate: b.query<TenderEstimateDetail, string>({
      query: (id) => `/agents/smeta/tender-estimates/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'TenderEstimate', id }],
    }),
    createTenderEstimate: b.mutation<{ check_id: string; status: string }, FormData>({
      query: (form) => ({ url: '/agents/smeta/tender-estimates', method: 'POST', body: form }),
      invalidatesTags: ['TenderEstimate'],
    }),
    deleteTenderEstimate: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/tender-estimates/${id}`, method: 'DELETE' }),
      invalidatesTags: ['TenderEstimate'],
    }),
    analyzeTenderEstimate: b.mutation<{ queued: boolean; check_id: string }, string>({
      query: (id) => ({ url: `/agents/smeta/tender-estimates/${id}/analyze`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'TenderEstimate', id }],
    }),
    annotateTenderIssue: b.mutation<
      { ok: boolean },
      { id: string; estimateId: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/smeta/tender-issues/${id}`,
        method: 'PATCH',
        body: { decision, comment },
      }),
      invalidatesTags: (_r, _e, { estimateId }) => [{ type: 'TenderEstimate', id: estimateId }],
    }),
    /** Решение сметчика по шифру позиции: ввести вручную или сбросить к
     *  автоматике (`code: null`). Пересчитывается только эта позиция и итоги
     *  сессии — полный `/analyze` для этого не годится, он стирает разметку и
     *  заново гоняет модель по всем позициям без шифра. */
    setTenderItemCode: b.mutation<
      TenderItemCodeResult,
      { estimateId: string; itemId: string; code: string | null; note?: string }
    >({
      query: ({ estimateId, itemId, code, note }) => ({
        url: `/agents/smeta/tender-estimates/${estimateId}/items/${itemId}/code`,
        method: 'PATCH',
        body: { code, note },
      }),
      invalidatesTags: (_r, _e, { estimateId }) => [{ type: 'TenderEstimate', id: estimateId }],
    }),
    /** Нормы расхода материалов: правила договора вместе с общими классами. */
    listContractMaterialNorms: b.query<{ norms: ContractMaterialNorm[] }, string>({
      query: (contractId) => ({ url: `/contracts/${contractId}/material-norms` }),
      providesTags: ['MaterialNorm'],
    }),
    /** Только общие классы — те, что действуют для всех договоров. */
    listGlobalMaterialNorms: b.query<{ norms: ContractMaterialNorm[] }, void>({
      query: () => ({ url: '/material-norms' }),
      providesTags: ['MaterialNorm'],
    }),
    createMaterialNorm: b.mutation<
      { ok: boolean; norm: ContractMaterialNorm },
      { contractId: string | null; body: Record<string, unknown> }
    >({
      query: ({ contractId, body }) => ({
        url: contractId ? `/contracts/${contractId}/material-norms` : '/material-norms',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MaterialNorm'],
    }),
    updateMaterialNorm: b.mutation<
      { ok: boolean; norm: ContractMaterialNorm },
      { normId: string; body: Record<string, unknown> }
    >({
      query: ({ normId, body }) => ({
        url: `/material-norms/${normId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['MaterialNorm'],
    }),
    deleteMaterialNorm: b.mutation<{ ok: boolean }, string>({
      query: (normId) => ({ url: `/material-norms/${normId}`, method: 'DELETE' }),
      invalidatesTags: ['MaterialNorm'],
    }),
    /** Какая норма подставится этому наименованию — проверка до списания. */
    resolveMaterialNorm: b.mutation<
      { matched: boolean; norm: ContractMaterialNorm | null; rules_considered: number },
      { contractId: string; body: { name: string; supply?: string; code?: string } }
    >({
      query: ({ contractId, body }) => ({
        url: `/contracts/${contractId}/material-norms/resolve`,
        method: 'POST',
        body,
      }),
    }),
    /** Вывести позицию из «Итого» (материал или оборудование поставки
     *  Заказчика) либо вернуть обратно. Стоимость позиции сохраняется —
     *  меняется только участие в сумме. */
    setTenderItemExclusion: b.mutation<
      TenderItemCodeResult,
      {
        estimateId: string;
        itemId: string;
        excluded: boolean;
        reason?: string | null;
        note?: string | null;
      }
    >({
      query: ({ estimateId, itemId, excluded, reason, note }) => ({
        url: `/agents/smeta/tender-estimates/${estimateId}/items/${itemId}/exclusion`,
        method: 'PATCH',
        body: { excluded, reason: reason ?? null, note: note ?? null },
      }),
      invalidatesTags: (_r, _e, { estimateId }) => [{ type: 'TenderEstimate', id: estimateId }],
    }),
    /** Принять подсказку автоподбора: шифр берётся из замечания на сервере и
     *  реально применяется к расчёту. Отличается от annotateTenderIssue, который
     *  только помечает замечание и сумму не меняет. */
    acceptTenderSuggestion: b.mutation<
      TenderItemCodeResult,
      { estimateId: string; itemId: string; issueId: string }
    >({
      query: ({ estimateId, itemId, issueId }) => ({
        url: `/agents/smeta/tender-estimates/${estimateId}/items/${itemId}/accept-suggestion`,
        method: 'POST',
        body: { issue_id: issueId },
      }),
      invalidatesTags: (_r, _e, { estimateId }) => [{ type: 'TenderEstimate', id: estimateId }],
    }),
    /** Подсказки расценок по наименованию — лениво, по вводу сметчика. Кэш не
     *  нужен: запрос дешёвый, а список зависит от строки поиска. */
    searchRateCandidates: b.query<
      RateCandidates,
      { q: string; region?: string; object_type?: string; limit?: number }
    >({
      query: (params) => ({ url: '/agents/smeta/rate-candidates', params }),
    }),
    /** Курация когорты: пометить расчёт доверенным аналогом (или снять). Флип
     *  eligibility меняет когорту/полосы во всех карточках bucket'а → инвалидируем
     *  весь тег TenderEstimate, не только id. */
    setTenderEligible: b.mutation<
      { ok: boolean; eligible: boolean },
      { id: string; eligible: boolean }
    >({
      query: ({ id, eligible }) => ({
        url: `/agents/smeta/tender-estimates/${id}/eligible`,
        method: 'PATCH',
        body: { eligible },
      }),
      invalidatesTags: ['TenderEstimate'],
    }),
    /** Выгрузка XLSX сформированного ЛСР (Функция 5). Лениво — строго по клику. */
    exportTenderEstimateXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/smeta/tender-estimates/${id}/export-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ОСНОВА.Сметчик, Функция 6 — проверка ЗВИ (Запрос на Внесение Изменения, ADR-0008).
    // Статус — поллингом getZviCheck (поле check.status), как tender/rate.
    listZviChecks: b.query<{ items: ZviCheck[] }, { limit?: number; offset?: number } | void>({
      query: (p) => ({ url: '/agents/smeta/zvi-checks', params: p ?? undefined }),
      providesTags: ['ZviCheck'],
    }),
    getZviCheck: b.query<ZviCheckDetail, string>({
      query: (id) => `/agents/smeta/zvi-checks/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'ZviCheck', id }],
    }),
    // MULTIPART: body — голый FormData (форма ЗВИ `file` + опц. `bom`/`boq`), без headers.
    createZviCheck: b.mutation<{ check_id: string; status: string }, FormData>({
      query: (form) => ({ url: '/agents/smeta/zvi-checks', method: 'POST', body: form }),
      invalidatesTags: ['ZviCheck'],
    }),
    deleteZviCheck: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/zvi-checks/${id}`, method: 'DELETE' }),
      invalidatesTags: ['ZviCheck'],
    }),
    analyzeZviCheck: b.mutation<{ queued: boolean; check_id: string }, string>({
      query: (id) => ({ url: `/agents/smeta/zvi-checks/${id}/analyze`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'ZviCheck', id }],
    }),
    // HITL-аннотация: PATCH по discrepancy-id, JSON body, инвалидирует родителя.
    annotateZviDiscrepancy: b.mutation<
      { ok: boolean },
      { id: string; checkId: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/smeta/zvi-discrepancies/${id}`,
        method: 'PATCH',
        body: { decision, comment },
      }),
      invalidatesTags: (_r, _e, { checkId }) => [{ type: 'ZviCheck', id: checkId }],
    }),
    // BLOB-export: query (lazy), responseHandler → blob, cache no-store.
    exportZviCheckXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/smeta/zvi-checks/${id}/export-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ОСНОВА.Сметчик, Функция 8 — сверка Генподряд↔Субподряд (ADR-0009).
    // Загрузка двух сторон (FormData поля gp/sp) → анализ (async Job) →
    // расхождения + маржа + покрытие + XLSX. Статус — поллингом getGsCheck.
    listGsChecks: b.query<{ items: GsCheck[] }, { limit?: number; offset?: number } | void>({
      query: (p) => ({ url: '/agents/smeta/gs-checks', params: p ?? undefined }),
      providesTags: ['GsCheck'],
    }),
    getGsCheck: b.query<GsCheckDetail, string>({
      query: (id) => `/agents/smeta/gs-checks/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'GsCheck', id }],
    }),
    createGsCheck: b.mutation<
      {
        check_id: string;
        gp_position_count: number;
        sp_position_count: number;
        rollup_count: number;
        warnings?: string[];
      },
      FormData
    >({
      query: (form) => ({ url: '/agents/smeta/gs-checks', method: 'POST', body: form }),
      invalidatesTags: ['GsCheck'],
    }),
    deleteGsCheck: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/smeta/gs-checks/${id}`, method: 'DELETE' }),
      invalidatesTags: ['GsCheck'],
    }),
    analyzeGsCheck: b.mutation<{ queued: boolean; check_id: string }, string>({
      query: (id) => ({ url: `/agents/smeta/gs-checks/${id}/analyze`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'GsCheck', id },
        { type: 'GsDiscrepancy', id },
      ],
    }),
    listGsDiscrepancies: b.query<GsDiscrepanciesResponse, string>({
      query: (id) => `/agents/smeta/gs-checks/${id}/discrepancies`,
      providesTags: (_r, _e, id) => [{ type: 'GsDiscrepancy', id }],
    }),
    annotateGsDiscrepancy: b.mutation<
      { ok: boolean },
      { id: string; checkId: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/smeta/gs-discrepancies/${id}`,
        method: 'PATCH',
        body: { decision, comment },
      }),
      invalidatesTags: (_r, _e, { checkId }) => [
        { type: 'GsDiscrepancy', id: checkId },
        { type: 'GsCheck', id: checkId },
      ],
    }),
    exportGsCheckXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/smeta/gs-checks/${id}/export-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ОСНОВА.Сметчик, Функция 9 — чат-ассистент поверх всех функций (ADR-0010).
    // Многоходовой read-only диалог; ответ приходит целиком (стрима у DeepSeek
    // нет) — UI показывает индикатор ожидания.
    listSmetaChatSessions: b.query<{ items: SmetaChatSession[] }, void>({
      query: () => '/agents/smeta/chat/sessions',
      providesTags: ['SmetaChatSession'],
    }),

    /** Сообщения сессии в хронологическом порядке. */
    listSmetaChatMessages: b.query<{ items: SmetaChatMessage[] }, string>({
      query: (sessionId) => `/agents/smeta/chat/sessions/${sessionId}/messages`,
      providesTags: (_r, _e, sessionId) => [{ type: 'SmetaChatMessages', id: sessionId }],
    }),

    /** Создать пустую сессию («Новый чат»). */
    createSmetaChatSession: b.mutation<{ session: SmetaChatSession }, void>({
      query: () => ({ url: '/agents/smeta/chat/sessions', method: 'POST' }),
      invalidatesTags: ['SmetaChatSession'],
    }),

    /** Задать вопрос. При отсутствии `session_id` backend создаёт новую сессию и
     *  возвращает её id + сохранённое сообщение ассистента. Инвалидируем список
     *  сессий (порядок по активности) и сообщения сессии. */
    askSmetaChat: b.mutation<
      { session_id: string; message: SmetaChatMessage },
      { question: string; session_id?: string }
    >({
      query: ({ question, session_id }) => ({
        url: '/agents/smeta/chat',
        method: 'POST',
        body: { question, session_id },
      }),
      invalidatesTags: (res) => {
        const tags: Array<'SmetaChatSession' | { type: 'SmetaChatMessages'; id: string }> = [
          'SmetaChatSession',
        ];
        if (res) tags.push({ type: 'SmetaChatMessages', id: res.session_id });
        return tags;
      },
    }),

    /** Переименовать сессию. */
    renameSmetaChatSession: b.mutation<{ ok: boolean }, { sessionId: string; title: string }>({
      query: ({ sessionId, title }) => ({
        url: `/agents/smeta/chat/sessions/${sessionId}`,
        method: 'PATCH',
        body: { title },
      }),
      invalidatesTags: ['SmetaChatSession'],
    }),

    /** Удалить сессию. */
    deleteSmetaChatSession: b.mutation<{ ok: boolean }, string>({
      query: (sessionId) => ({
        url: `/agents/smeta/chat/sessions/${sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SmetaChatSession'],
    }),

    // ===== ОСНОВА.Инженер (ИИ-агент ПТО) =====

    /** §8.4 Этап 3b: предпросмотр ОЖР за период (титул + строки раздела 3).
     *  Тот же payload уходит в рендер DOCX — витрина = точный черновик. */
    listOzhrEntries: b.query<
      OzhrPayload,
      { projectId: string; from?: string; to?: string }
    >({
      query: ({ projectId, from, to }) => ({
        url: `/agents/pto/projects/${projectId}/ozhr/entries`,
        params: { from: from || undefined, to: to || undefined },
      }),
      providesTags: ['PtoOzhr'],
    }),
    /** Сохранить реквизиты застройки ОЖР (metadata.ozhr). Инвалидирует проект. */
    setOzhrRequisites: b.mutation<
      { project: PtoProject },
      { projectId: string; requisites: OzhrRequisites }
    >({
      query: ({ projectId, requisites }) => ({
        url: `/agents/pto/projects/${projectId}/ozhr/requisites`,
        method: 'PATCH',
        body: requisites,
      }),
      invalidatesTags: ['PtoProject'],
    }),
    /** Выгрузка DOCX чернового ОЖР за период (форма 1026/пр). Лениво, по клику. */
    exportOzhrDocx: b.query<Blob, { projectId: string; from?: string; to?: string }>({
      query: ({ projectId, from, to }) => ({
        url: `/agents/pto/projects/${projectId}/ozhr/docx`,
        params: { from: from || undefined, to: to || undefined },
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** Сгенерировать черновой КС-2 за период из принятых фактов проекта. */
    generateKsDraft: b.mutation<KsDraftDetail, { projectId: string; from: string; to: string }>({
      query: ({ projectId, from, to }) => ({
        url: `/agents/pto/projects/${projectId}/ks-drafts`,
        method: 'POST',
        body: { from, to },
      }),
      invalidatesTags: ['PtoKsDraft'],
    }),
    /** Список черновиков КС-2 проекта. */
    listKsDrafts: b.query<{ drafts: FieldKsDraft[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/ks-drafts`,
      providesTags: ['PtoKsDraft'],
    }),
    /** Черновик КС-2 со строками, сводкой и предложенными позициями сметы. */
    getKsDraft: b.query<KsDraftDetail, string>({
      query: (id) => `/agents/pto/ks-drafts/${id}`,
      providesTags: ['PtoKsDraft'],
    }),
    /** Решение инженера по строке черновика: позиция сметы (`estimate_item_id`)
     *  или «нет в смете» (`null`); `scope` — вся работа (по умолчанию) или
     *  строка одного факта. Сервер пересобирает черновик и отдаёт его. */
    setKsItemMatch: b.mutation<
      KsDraftDetail,
      { itemId: string; estimate_item_id: string | null; scope: 'work' | 'line' }
    >({
      query: ({ itemId, estimate_item_id, scope }) => ({
        url: `/agents/pto/ks-draft-items/${itemId}/match`,
        method: 'POST',
        body: { estimate_item_id, scope },
      }),
      invalidatesTags: ['PtoKsDraft'],
    }),
    /** Снять решение инженера (строки снова матчатся автоматически). */
    resetKsItemMatch: b.mutation<KsDraftDetail, { itemId: string; scope: 'work' | 'line' }>({
      query: ({ itemId, scope }) => ({
        url: `/agents/pto/ks-draft-items/${itemId}/match`,
        method: 'DELETE',
        params: { scope },
      }),
      invalidatesTags: ['PtoKsDraft'],
    }),
    /** Позиции активной сметы договора для выбора в строке черновика. */
    listKsEstimateItems: b.query<
      { estimate_id: string; version_no: number; items: KsEstimateItemOption[] },
      { draftId: string; q?: string }
    >({
      query: ({ draftId, q }) => ({
        url: `/agents/pto/ks-drafts/${draftId}/estimate-items`,
        params: { q: q || undefined, limit: 50 },
      }),
    }),
    /** Договор, к которому привязан проект ПТО (номер, контрагент, смета). */
    getPtoProjectContract: b.query<
      { contract_id: string | null; contract: PtoContractBrief | null },
      string
    >({
      query: (projectId) => `/agents/pto/projects/${projectId}/contract`,
      providesTags: ['PtoProject'],
    }),
    /** Договоры для привязки проекта (нужен доступ к договорам или сметам). */
    listPtoContractOptions: b.query<
      { items: PtoContractBrief[] },
      { projectId: string; q?: string }
    >({
      query: ({ projectId, q }) => ({
        url: `/agents/pto/projects/${projectId}/contract-options`,
        params: { q: q || undefined, limit: 50 },
      }),
    }),
    /** Выгрузка XLSX чернового КС-2. Лениво, по клику. */
    exportKsDraftXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/pto/ks-drafts/${id}/xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ===== Сквозная интеграция «единого организма» ОСНОВА.Сметчик =====

    /** Договоры, проекты ПТО и преддоговоры для выбора привязки в формах Сметчика. */
    listSmetaContextOptions: b.query<SmetaContextOptions, void>({
      query: () => '/agents/smeta/context-options',
    }),

    /** Привязать ленту накопительного учёта ПА (Функция 7B) к договору Статута и/или проекту ПТО. */
    bindAccumulationContract: b.mutation<
      { ledger: AccumLedger },
      { id: string; statut_contract_id?: string | null; project_id?: string | null }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/smeta/accumulations/${id}/contract`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AccumLedger', id }, 'AccumLedger'],
    }),

    /** Импортировать смету из карточки договора (contract_version_files с document_kind='estimate')
     *  в эталон Сметчика (contract_estimate и/или contract_lsr) без повторной ручной загрузки файла. */
    importContractVersionEstimate: b.mutation<
      {
        contract_id: string;
        file_id: string;
        filename: string;
        target: string;
        estimate_version_no: number | null;
        estimate_items_count: number;
        imported_lsr_count: number;
        imported_lsr_items: number;
      },
      {
        contractId: string;
        file_id?: string;
        version_file_id?: string;
        target?: 'estimate' | 'lsr' | 'both';
        mode?: 'estimate' | 'lsr' | 'both';
        source_format?: string;
        is_amendment?: boolean;
        region?: string;
        price_level_quarter?: string;
        notes?: string;
        comment?: string;
      }
    >({
      query: ({ contractId, ...body }) => ({
        url: `/agents/smeta/contracts/${contractId}/import-version-estimate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { contractId }) => [
        'ContractEstimate',
        { type: 'ContractLsr', id: contractId },
      ],
    }),

    /** Отправить межфункциональную заявку из Сметчика в Юриста (Статут) или Экономиста:
     *  contract_price_revision | legal_claim | budget_limit_exceeded. */
    createSmetaRequest: b.mutation<
      { request_id: string; status: string },
      {
        kind: 'contract_price_revision' | 'legal_claim' | 'budget_limit_exceeded';
        contract_id?: string;
        project_id?: string;
        title?: string;
        summary: string;
        details?: Record<string, unknown>;
        assigned_to?: string;
      }
    >({
      query: (body) => ({
        url: '/agents/smeta/requests',
        method: 'POST',
        body,
      }),
    }),

    /** Перевести собранный пакет акта КС в статус issued (выпущен заказчику / для БДР Экономиста). */
    issueKsPackage: b.mutation<{ package: KsPackage }, string>({
      query: (id) => ({
        url: `/agents/smeta/ks-packages/${id}/issue`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'KsPackage', id }, 'KsPackage'],
    }),

    /** Втянуть приёмки поставок из МТО (mto_deliveries) в первичку пакета КС. */
    importKsPackageDocsFromMto: b.mutation<
      { imported_docs: number; matched: number; unmatched: number; rebuild_required: boolean },
      string
    >({
      query: (id) => ({
        url: `/agents/smeta/ks-packages/${id}/documents/from-mto`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'KsPackageDoc', id },
        { type: 'KsPackageMaterial', id },
        { type: 'KsPackageIssue', id },
        { type: 'KsPackage', id },
      ],
    }),

    /** Втянуть завершённые рейсы из Логиста (logist_trips) в компенсируемые затраты пакета КС. */
    importKsPackageTripsFromLogist: b.mutation<
      { imported_trips: number; rebuild_required: boolean },
      string
    >({
      query: (id) => ({
        url: `/agents/smeta/ks-packages/${id}/trips/from-logist`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'KsPackage', id }],
    }),

    /** Синхронизировать нормативную трудоёмкость из локальных смет (contract_lsr_items) в работы КСП. */
    syncContractLsrKspLabor: b.mutation<
      { synced_activities: number; total_lsr_items: number; total_activities: number },
      { contractId: string; project_id?: string }
    >({
      query: ({ contractId, ...body }) => ({
        url: `/agents/smeta/contracts/${contractId}/lsr/sync-ksp-labor`,
        method: 'POST',
        body,
      }),
    }),

    /** Двусторонне синхронизировать contract_estimate ↔ contract_lsr и выгрузить материалы в нормы М-29 и ведомость МТО. */
    syncContractLsrMaterials: b.mutation<
      {
        bridged_to_estimate: number;
        bridged_to_lsr: number;
        synced_norms: number;
        synced_mto_positions: number;
      },
      { contractId: string }
    >({
      query: ({ contractId }) => ({
        url: `/agents/smeta/contracts/${contractId}/lsr/sync-materials`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { contractId }) => [
        { type: 'ContractLsr', id: contractId },
        { type: 'ContractEstimate', id: contractId },
        'MaterialNorm',
      ],
    }),
    /** Передать черновик КС-2 из ПТО напрямую в Пакет акта КС-2/КС-3 Сметчика. */
    pushKsDraftToSmeta: b.mutation<
      { ok: boolean; package: KsPackage; act: KsPackageAct; build_queued: boolean },
      { draftId: string }
    >({
      query: ({ draftId }) => ({
        url: `/agents/pto/ks-drafts/${draftId}/push-to-smeta`,
        method: 'POST',
      }),
      invalidatesTags: ['KsPackage', 'PtoKsDraft'],
    }),

    /** Импортировать данные выпущенного пакета КС-3 в ленту Накопительного учёта ПА (Функция 7B). */
    importAccumulationFromKsPackage: b.mutation<
      {
        ledger_id: string;
        package_id: string;
        imported_entries: number;
        imported_unit_rates: number;
      },
      { id: string; package_id: string }
    >({
      query: ({ id, package_id }) => ({
        url: `/agents/smeta/accumulations/${id}/import-from-ks-package`,
        method: 'POST',
        body: { package_id },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AccumLedger', id }, 'AccumLedger'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSmetaInfoQuery,
  useListVolumeChecksQuery,
  useGetVolumeCheckQuery,
  useCreateVolumeCheckMutation,
  useDeleteVolumeCheckMutation,
  useAnnotateVolumeDiscrepancyMutation,
  useLazyExportVolumeCheckXlsxQuery,
  useListWriteOffChecksQuery,
  useGetWriteOffCheckQuery,
  useCreateWriteOffCheckMutation,
  useDeleteWriteOffCheckMutation,
  useAnnotateWriteOffDiscrepancyMutation,
  useLazyExportWriteOffCheckXlsxQuery,
  useListRegistersQuery,
  useGetRegisterQuery,
  useCreateRegisterMutation,
  useDeleteRegisterMutation,
  useLazyExportRegisterXlsxQuery,
  useListAccumulationsQuery,
  useGetAccumulationQuery,
  useSeedAccumulationMutation,
  useUploadAccumulationPackMutation,
  useGetAccumulationUploadQuery,
  useListAccumulationEntriesQuery,
  useSetAccumulationEntriesMarkMutation,
  useDeleteAccumulationMutation,
  useDeleteAccumulationUploadMutation,
  useLazyExportAccumulationXlsxQuery,
  useListFgiscsPeriodsQuery,
  useGetFgiscsPeriodDiffQuery,
  useActivateFgiscsPeriodMutation,
  useRestoreArchivedFgiscsPeriodMutation,
  useActivateAllFgiscsPeriodsMutation,
  useListRimRegionsQuery,
  useListObjectTypesQuery,
  useListFsnbEditionsQuery,
  useUploadFsnbEditionMutation,
  useListFsnbEditionChangesQuery,
  useActivateFsnbEditionMutation,
  useDeleteFsnbEditionMutation,
  useRetryFsnbEditionMutation,
  useSearchFsnbWorksQuery,
  useGetFsnbWorkQuery,
  useSearchFsnbResourcesQuery,
  useGetFsnbResourceQuery,
  useSearchFsnbCoefficientsQuery,
  useGetFsnbCoefficientQuery,
  useListFsnbRegistryQuery,
  useCheckFsnbRegistryMutation,
  useListRateChecksQuery,
  useGetRateCheckQuery,
  useUploadRateCheckMutation,
  useDeleteRateCheckMutation,
  useAnalyzeRateCheckMutation,
  useListRateDiscrepanciesQuery,
  useAnnotateRateDiscrepancyMutation,
  useLazyExportRateCheckXlsxQuery,
  useListContractEstimatesQuery,
  useListContractEstimateOptionsQuery,
  useUploadContractEstimateMutation,
  useListKsActsQuery,
  useGetKsActQuery,
  useUploadKsActMutation,
  useDeleteKsActMutation,
  useAnalyzeKsActMutation,
  useListKsDiscrepanciesQuery,
  useAnnotateKsDiscrepancyMutation,
  useLazyExportKsCheckXlsxQuery,
  useListKsPackagesQuery,
  useGetKsPackageQuery,
  useCreateKsPackageMutation,
  useDeleteKsPackageMutation,
  useBuildKsPackageMutation,
  useUpsertKsPackageActMutation,
  useDeleteKsPackageActMutation,
  useListKsPackageLinesQuery,
  useListKsPackageIssuesQuery,
  useAnnotateKsPackageIssueMutation,
  useMatchKsPackageLineMutation,
  useListKsPackageOverridesQuery,
  useUpsertKsPackageOverrideMutation,
  useDeleteKsPackageOverrideMutation,
  useListKsPackageMaterialsQuery,
  useListKsPackageDocsQuery,
  useUploadKsPackageDocsMutation,
  useListKsPackageFormsQuery,
  useRenderKsPackageActFormsMutation,
  useRenderKsPackageFormsMutation,
  useListTenderEstimatesQuery,
  useGetTenderEstimateQuery,
  useCreateTenderEstimateMutation,
  useDeleteTenderEstimateMutation,
  useAnalyzeTenderEstimateMutation,
  useAnnotateTenderIssueMutation,
  useSetTenderItemCodeMutation,
  useSetTenderItemExclusionMutation,
  useListContractMaterialNormsQuery,
  useListGlobalMaterialNormsQuery,
  useCreateMaterialNormMutation,
  useUpdateMaterialNormMutation,
  useDeleteMaterialNormMutation,
  useResolveMaterialNormMutation,
  useAcceptTenderSuggestionMutation,
  useLazySearchRateCandidatesQuery,
  useSetTenderEligibleMutation,
  useLazyExportTenderEstimateXlsxQuery,
  useListZviChecksQuery,
  useGetZviCheckQuery,
  useCreateZviCheckMutation,
  useDeleteZviCheckMutation,
  useAnalyzeZviCheckMutation,
  useAnnotateZviDiscrepancyMutation,
  useLazyExportZviCheckXlsxQuery,
  useListSmetaChatSessionsQuery,
  useListSmetaChatMessagesQuery,
  useCreateSmetaChatSessionMutation,
  useAskSmetaChatMutation,
  useRenameSmetaChatSessionMutation,
  useDeleteSmetaChatSessionMutation,
  useListOzhrEntriesQuery,
  useSetOzhrRequisitesMutation,
  useLazyExportOzhrDocxQuery,
  useGenerateKsDraftMutation,
  useListKsDraftsQuery,
  useGetKsDraftQuery,
  useLazyExportKsDraftXlsxQuery,
  useSetKsItemMatchMutation,
  useResetKsItemMatchMutation,
  useListKsEstimateItemsQuery,
  useGetPtoProjectContractQuery,
  useListPtoContractOptionsQuery,
  useListGsChecksQuery,
  useGetGsCheckQuery,
  useCreateGsCheckMutation,
  useDeleteGsCheckMutation,
  useAnalyzeGsCheckMutation,
  useListGsDiscrepanciesQuery,
  useAnnotateGsDiscrepancyMutation,
  useLazyExportGsCheckXlsxQuery,
  useListContractLsrQuery,
  useListContractLsrItemsQuery,
  useImportContractLsrMutation,
  useDeleteContractLsrMutation,
  useListSmetaContextOptionsQuery,
  useBindAccumulationContractMutation,
  useImportContractVersionEstimateMutation,
  useCreateSmetaRequestMutation,
  useIssueKsPackageMutation,
  useImportKsPackageDocsFromMtoMutation,
  useImportKsPackageTripsFromLogistMutation,
  useSyncContractLsrKspLaborMutation,
  useSyncContractLsrMaterialsMutation,
  usePushKsDraftToSmetaMutation,
  useImportAccumulationFromKsPackageMutation,
} = smetaApi;
